import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button, Input, Label, Spinner,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@agion/ui'
import { ApiError } from '@/core/infra_api_client'
import { usePlatformAdmin } from '@/hooks/infra_use_platform_admin'
import type { CloudEnvironment } from '@/core/infra_types'
import {
  inspectFailedCleanup, submitFailedCleanup,
  type FailedCleanupPreview, type FailedCleanupRequest,
} from './infra_failed_cleanup.service'

interface Props {
  environment: CloudEnvironment
  onOperation: (id: string) => void
}

export function FailedProvisionCleanup({ environment, onOperation }: Props) {
  const { isPlatformAdmin, isLoading } = usePlatformAdmin()
  if (isLoading || !isPlatformAdmin) return null
  return <AuthorizedFailedProvisionCleanup key={`${environment.organizationId}/${environment.id}`} environment={environment} onOperation={onOperation} />
}

function AuthorizedFailedProvisionCleanup({ environment, onOperation }: Props) {
  const id = useId()
  const { t } = useTranslation('pages')
  const [open, setOpen] = useState(false)
  const [credentials, setCredentials] = useState<{ id: string; name: string }[]>([])
  const [credential, setCredential] = useState('')
  const [preview, setPreview] = useState<FailedCleanupPreview | null>(null)
  const [confirmation, setConfirmation] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [retry, setRetry] = useState<FailedCleanupRequest | null>(null)
  const sequence = useRef(0)
  const invalidate = useCallback(() => { ++sequence.current }, [])
  const identity = `${environment.organizationId}/${environment.id}`

  useEffect(() => {
    const current = ++sequence.current
    setPreview(null)
    setSubmitting(false)
    setCredential('')
    setConfirmation('')
    setRetry(null)
    setError(null)
    setCredentials([])
    if (!open) return
    setLoading(true)
    void inspectFailedCleanup(environment.id, '').then(result => {
      if (sequence.current !== current) return
      setCredentials(result.credentials ?? [])
      setPreview(result)
    }).catch(() => {
      if (sequence.current === current) setError(t('environments.cleanup.unavailable'))
    }).finally(() => {
      if (sequence.current === current) setLoading(false)
    })
    return invalidate
  }, [open, identity, environment.id, t, invalidate])

  function changeCredential(value: string) {
    ++sequence.current
    setCredential(value)
    setPreview(null)
    setConfirmation('')
    setRetry(null)
    setError(null)
    setLoading(false)
  }

  async function inspect() {
    const current = ++sequence.current
    setPreview(null)
    setConfirmation('')
    setRetry(null)
    setError(null)
    setLoading(true)
    try {
      const result = await inspectFailedCleanup(environment.id, credential)
      if (sequence.current === current) setPreview(result)
    } catch {
      if (sequence.current === current) setError(t('environments.cleanup.unavailable'))
    } finally {
      if (sequence.current === current) setLoading(false)
    }
  }

  async function submit() {
    if (!preview?.allowed || !preview.fingerprint) return
    const request = retry ?? {
      requestId: crypto.randomUUID(), credentialId: credential,
      fingerprint: preview.fingerprint, confirmation,
    }
    const current = ++sequence.current
    setSubmitting(true)
    setError(null)
    try {
      const operation = await submitFailedCleanup(environment.id, request)
      if (sequence.current !== current) return
      setOpen(false)
      onOperation(operation.id)
    } catch (cause) {
      if (sequence.current !== current) return
      if (cause instanceof ApiError && cause.status === 409) {
        setPreview(null)
        setRetry(null)
        setError(t('environments.cleanup.changed'))
      } else {
        // A lost response can still have queued the operation. Retry the exact
        // request identity; the server returns its original operation.
        setRetry(request)
        setError(t('environments.cleanup.retryNotice'))
      }
    } finally {
      if (sequence.current === current) setSubmitting(false)
    }
  }

  return <>
    <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
      {t('environments.cleanup.inspectAction')}
    </Button>
    <Dialog open={open} onOpenChange={value => { if (!submitting) setOpen(value) }}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('environments.cleanup.title')}</DialogTitle>
          <DialogDescription>{t('environments.cleanup.description')} {t('environments.cleanup.recordOnly')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <p className="break-all text-sm font-medium">{environment.name}</p>
          <p className="break-all text-xs text-muted-foreground">{environment.id}</p>
          <Label htmlFor={`${id}-credential`}>{t('environments.cleanup.credential')}</Label>
          <Select value={credential} onValueChange={changeCredential} disabled={submitting || !!retry || credentials.length === 0}>
            <SelectTrigger id={`${id}-credential`}><SelectValue placeholder={t('environments.cleanup.selectCredential')} /></SelectTrigger>
            <SelectContent>{credentials.map(item => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent>
          </Select>
          {loading && <p role="status" className="flex items-center gap-2"><Spinner aria-hidden="true" />{t('environments.cleanup.checking')}</p>}
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
          {preview && <div className="space-y-2 text-sm" aria-live="polite">
            <p className="break-all">{t('environments.cleanup.host')}: {preview.hostName ? `${preview.hostName} (${preview.hostId})` : preview.hostId}</p>
            {preview.account && <p>{t('environments.cleanup.account')}: {preview.account}</p>}
            {preview.blockers.length > 0 && <ul className="list-disc space-y-1 pl-5">{preview.blockers.map((blocker, index) => <li key={index}>{t(`environments.cleanup.blockers.${preview.blockerCode}`, { defaultValue: blocker })}</li>)}</ul>}
            {preview.resources.length > 0 && <section aria-label={t('environments.cleanup.resourcesFound')}>
              <p className="font-medium">{t('environments.cleanup.resourcesFound')}</p>
              <ul className="list-disc space-y-1 pl-5">{preview.resources.map(resource => <li key={`${resource.kind}/${resource.id}`} className="break-all">{resource.name} ({resource.id})</li>)}</ul>
            </section>}
            {preview.allowed && <>
              <p>{t('environments.cleanup.ready')}</p>
              <p id={`${id}-consequences`}>{t('environments.cleanup.consequences')}</p>
              <Label htmlFor={`${id}-name`}>{t('environments.cleanup.confirm', { name: preview.environmentName })}</Label>
              <Input id={`${id}-name`} aria-describedby={`${id}-consequences`} value={confirmation} onChange={e => setConfirmation(e.target.value)} disabled={submitting || !!retry} autoComplete="off" />
            </>}
          </div>}
        </div>
        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button variant="outline" disabled={submitting} onClick={() => setOpen(false)}>{t('environments.cleanup.cancel')}</Button>
          <Button variant="outline" disabled={!credential || loading || submitting || !!retry} onClick={() => void inspect()}>{t('environments.cleanup.check')}</Button>
          <Button variant="destructive" disabled={!credential || !preview?.allowed || !preview.fingerprint || confirmation !== preview.environmentName || loading || submitting} onClick={() => void submit()}>
            {submitting ? <Spinner size="sm" /> : t(retry ? 'environments.cleanup.retry' : 'environments.cleanup.remove')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>
}
